import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { useCourierPermissions } from "@/courier/hooks/useCourierPermissions";
import type { TeamPermission } from "@/courier/lib/teamPermissions";

/**
 * Stops a courier team member from landing on a screen their account is not
 * allowed to use.
 *
 * This is presentation only. The database re-checks the same ability inside
 * every scoped function, so removing this component would not grant access —
 * it would only mean the person sees a screen that fails to load.
 */
interface RequirePermissionProps {
  perm: TeamPermission;
  children: React.ReactNode;
}

export const RequirePermission: React.FC<RequirePermissionProps> = ({ perm, children }) => {
  const { can, ready } = useCourierPermissions();
  const { t } = useTranslation();

  // Until the membership is known, render nothing rather than flashing either
  // the screen or the refusal.
  if (!ready) return null;
  if (can(perm)) return <>{children}</>;

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-md mx-auto mt-10 border border-white/10 bg-[var(--portal-surface)]/40 p-4 sm:p-6 text-center">
        <Lock className="h-6 w-6 mx-auto text-white/40" />
        <h2 className="mt-4 text-base font-semibold text-white">
          {t("courierTeam.noAccess.title", "You do not have access to this section")}
        </h2>
        <p className="mt-2 text-sm text-white/60">
          {t(
            "courierTeam.noAccess.body",
            "Ask the owner of this courier account to give you this ability in Team settings.",
          )}
        </p>
        <Link
          to="/courier"
          className="inline-block mt-5 px-4 py-2 text-sm font-medium border border-white/20 text-white hover:bg-white/5 transition-colors"
        >
          {t("courierTeam.noAccess.back", "Back to dashboard")}
        </Link>
      </div>
    </div>
  );
};

export default RequirePermission;
